import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Terminal, 
  RefreshCw, 
  Download, 
  Search, 
  Filter,
  Loader2,
  AlertCircle,
  Info,
  AlertTriangle,
  XCircle,
  Clock,
  Trash2,
  Play,
  Pause
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOG_TYPES = [
  { value: 'all', label: 'Tous les logs', file: 'all' },
  { value: 'backend', label: 'Backend (FastAPI)', file: 'backend' },
  { value: 'frontend', label: 'Frontend (React)', file: 'frontend' },
  { value: 'supervisor', label: 'Supervisor', file: 'supervisord' },
];

const LOG_LEVELS = ['ALL', 'ERROR', 'WARNING', 'INFO', 'DEBUG'];

const Logs = () => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('backend');
  const [selectedLevel, setSelectedLevel] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [linesCount, setLinesCount] = useState(200);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(5);
  
  const logsContainerRef = useRef(null);
  const intervalRef = useRef(null);
  
  const isSuperAdmin = user?.role === 'superadmin';

  useEffect(() => {
    if (isSuperAdmin) {
      fetchLogs();
    }
  }, [selectedType, isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchLogs(true);
      }, refreshInterval * 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, selectedType, isSuperAdmin]);

  const fetchLogs = async (silent = false) => {
    if (!isSuperAdmin) return;
    if (!silent) setLoading(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/v2/admin/logs?type=${selectedType}&lines=${linesCount}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        
        // Auto-scroll to bottom
        if (logsContainerRef.current) {
          logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
        }
      } else {
        if (!silent) toast.error('Erreur lors du chargement des logs');
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      if (!silent) toast.error('Erreur de connexion');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const clearLogs = async () => {
    if (!confirm('Êtes-vous sûr de vouloir effacer ces logs ?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/v2/admin/logs/clear?type=${selectedType}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        setLogs([]);
        toast.success('Logs effacés');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const downloadLogs = () => {
    const logText = logs.join('\n');
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedType}_logs_${new Date().toISOString().slice(0, 10)}.log`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Logs téléchargés');
  };

  const getLogLevel = (line) => {
    const lowerLine = line.toLowerCase();
    if (lowerLine.includes('error') || lowerLine.includes('exception') || lowerLine.includes('traceback')) {
      return 'ERROR';
    }
    if (lowerLine.includes('warning') || lowerLine.includes('warn')) {
      return 'WARNING';
    }
    if (lowerLine.includes('info')) {
      return 'INFO';
    }
    if (lowerLine.includes('debug')) {
      return 'DEBUG';
    }
    return 'INFO';
  };

  const getLogIcon = (level) => {
    switch (level) {
      case 'ERROR':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'WARNING':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'DEBUG':
        return <Clock className="w-4 h-4 text-slate-400" />;
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getLogClass = (level) => {
    switch (level) {
      case 'ERROR':
        return 'text-red-400 bg-red-500/10';
      case 'WARNING':
        return 'text-amber-400 bg-amber-500/10';
      case 'DEBUG':
        return 'text-slate-400';
      default:
        return isDarkMode ? 'text-slate-300' : 'text-slate-700';
    }
  };

  const filteredLogs = logs.filter(line => {
    // Filter by level
    if (selectedLevel !== 'ALL') {
      const level = getLogLevel(line);
      if (level !== selectedLevel) return false;
    }
    
    // Filter by search term
    if (searchTerm) {
      return line.toLowerCase().includes(searchTerm.toLowerCase());
    }
    
    return true;
  });

  // Access denied for non-superadmin
  if (!isSuperAdmin) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Card className="p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-bold mb-2">Accès Refusé</h2>
            <p className="text-slate-500">Cette page est réservée aux super-administrateurs.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="logs-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Logs Système
            </h1>
            <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
              Visualisez et analysez les logs de l'application
            </p>
          </div>
          <Badge variant="outline" className="text-indigo-600 border-indigo-600">
            <Terminal className="w-4 h-4 mr-1" />
            SUPADMIN
          </Badge>
        </div>

        {/* Controls */}
        <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              {/* Log Type Selection */}
              <div className="space-y-2">
                <Label>Type de log</Label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOG_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Level Filter */}
              <div className="space-y-2">
                <Label>Niveau</Label>
                <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOG_LEVELS.map(level => (
                      <SelectItem key={level} value={level}>
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Lines Count */}
              <div className="space-y-2">
                <Label>Lignes</Label>
                <Select value={String(linesCount)} onValueChange={(v) => setLinesCount(Number(v))}>
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                    <SelectItem value="1000">1000</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Search */}
              <div className="flex-1 min-w-[200px] space-y-2">
                <Label>Rechercher</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Filtrer les logs..."
                    className="pl-9"
                  />
                </div>
              </div>

              {/* Auto Refresh Toggle */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                  id="auto-refresh"
                />
                <Label htmlFor="auto-refresh" className="cursor-pointer">
                  {autoRefresh ? (
                    <span className="flex items-center gap-1 text-green-500">
                      <Play className="w-4 h-4" /> Auto ({refreshInterval}s)
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <Pause className="w-4 h-4" /> Manuel
                    </span>
                  )}
                </Label>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchLogs()}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadLogs}
                  disabled={logs.length === 0}
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearLogs}
                  className="text-red-500 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs Display */}
        <Card className={isDarkMode ? 'bg-[hsl(220,14%,10%)] border-[hsl(220,14%,22%)]' : 'bg-slate-900'}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
                <Terminal className="w-4 h-4" />
                {LOG_TYPES.find(t => t.value === selectedType)?.label}
              </CardTitle>
              <Badge variant="outline" className="text-xs">
                {filteredLogs.length} / {logs.length} lignes
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div
              ref={logsContainerRef}
              className="font-mono text-xs h-[500px] overflow-auto p-4 rounded-lg bg-black/50"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
            >
              {loading && logs.length === 0 ? (
                <div className="flex items-center justify-center h-full text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  Chargement des logs...
                </div>
              ) : filteredLogs.length === 0 ? (
                <div className="flex items-center justify-center h-full text-slate-500">
                  <Info className="w-6 h-6 mr-2" />
                  {logs.length === 0 ? 'Aucun log disponible' : 'Aucun résultat pour ce filtre'}
                </div>
              ) : (
                filteredLogs.map((line, index) => {
                  const level = getLogLevel(line);
                  return (
                    <div
                      key={index}
                      className={`py-0.5 px-2 rounded ${getLogClass(level)} hover:bg-white/5`}
                    >
                      <span className="inline-flex items-center gap-1 mr-2">
                        {getLogIcon(level)}
                        <span className="text-slate-500 text-[10px]">{String(index + 1).padStart(4, '0')}</span>
                      </span>
                      {line}
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-500">
                {logs.filter(l => getLogLevel(l) === 'ERROR').length}
              </div>
              <div className="text-xs text-slate-500">Erreurs</div>
            </CardContent>
          </Card>
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-amber-500">
                {logs.filter(l => getLogLevel(l) === 'WARNING').length}
              </div>
              <div className="text-xs text-slate-500">Warnings</div>
            </CardContent>
          </Card>
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-500">
                {logs.filter(l => getLogLevel(l) === 'INFO').length}
              </div>
              <div className="text-xs text-slate-500">Info</div>
            </CardContent>
          </Card>
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-slate-400">
                {logs.length}
              </div>
              <div className="text-xs text-slate-500">Total</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Logs;
